<template>
  <header class="stacked-header" :class="{ 'stacked-header--solid': isSolid }">
    <!-- Top utility bar -->
    <div class="stacked-header__topbar">
      <div class="container stacked-header__topbar-inner">
        <div class="stacked-header__contact">
          <a v-if="email" :href="`mailto:${email}`" class="stacked-header__contact-item">
            <icon-mail class="stacked-header__contact-icon" />
            <span dir="ltr">{{ email }}</span>
          </a>
          <div v-if="workingHours" class="stacked-header__contact-item stacked-header__hours">
            <icon-timer class="stacked-header__contact-icon" />
            <span>{{ t(workingHours) }}</span>
          </div>
        </div>
        <div class="stacked-header__socials">
          <a
            v-if="socials.instagram"
            :href="socials.instagram"
            target="_blank"
            rel="noopener"
            aria-label="Instagram"
          >
            <icon-instagram />
          </a>
          <a
            v-if="socials.linkedin"
            :href="socials.linkedin"
            target="_blank"
            rel="noopener"
            aria-label="LinkedIn"
          >
            <icon-linkedin />
          </a>
        </div>
      </div>
    </div>

    <!-- Main navigation bar -->
    <div class="stacked-header__nav">
      <div class="container stacked-header__nav-inner">
        <NuxtLink :to="localePath('/')" class="stacked-header__logo">
          <NuxtImg loading="lazy" v-no-right-click :src="logo" alt="Neo Tours & Events" />
        </NuxtLink>

        <div class="stacked-header__actions">
          <!-- Desktop links -->
          <nav class="stacked-header__links">
            <NuxtLink
              v-for="(link, index) in navLinks"
              :key="index"
              :to="localePath(link.target)"
              class="stacked-header__link"
            >
              {{ t(link.title) }}
              <icon-chevron-down v-if="link.hasDropdown" class="stacked-header__chevron" />
            </NuxtLink>
          </nav>

          <!-- Language switcher (visible on all breakpoints) -->
          <div
            v-if="isMultiLanguage && availableLocales.length > 1"
            v-click-outside="closeLang"
            class="stacked-header__lang"
          >
            <button type="button" class="stacked-header__link" @click="langOpen = !langOpen">
              {{ langLabel }}
              <icon-chevron-down
                class="stacked-header__chevron"
                :class="{ 'rotate-180': langOpen }"
              />
            </button>
            <ul v-if="langOpen" class="stacked-header__lang-menu">
              <li v-for="loc in availableLocales" :key="loc.code">
                <button
                  type="button"
                  :class="{ 'is-active': loc.code === locale }"
                  @click="selectLang(loc.code)"
                >
                  {{ loc.name }}
                </button>
              </li>
            </ul>
          </div>

          <!-- Mobile hamburger -->
          <button
            type="button"
            class="stacked-header__burger"
            :class="{ 'is-open': mobileOpen }"
            aria-label="Toggle menu"
            @click="mobileOpen = !mobileOpen"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <transition name="stacked-fade">
        <nav v-if="mobileOpen" class="stacked-header__mobile">
          <NuxtLink
            v-for="(link, index) in navLinks"
            :key="index"
            :to="localePath(link.target)"
            class="stacked-header__mobile-link"
            @click="mobileOpen = false"
          >
            {{ t(link.title) }}
          </NuxtLink>
        </nav>
      </transition>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRuntimeConfig } from "nuxt/app";

const props = defineProps({
  routePath: { type: String, default: "/" },
});
defineEmits(["open-login"]);

const config: any = useRuntimeConfig();
const localePath = useLocalePath();
const { t, locale, locales, setLocale } = useI18n();

const headerData = config.public.header ?? {};
const logo = headerData.logoDarkMode || headerData.logo;
const email = config.public.email || headerData.email || "";
const workingHours = headerData.workingHours || "";
const socials = config.public.footer?.socialLinks ?? {};
const navLinks = headerData.links ?? [];
const isMultiLanguage = headerData.isMultiLanguage ?? true;

const defaultLocale = config.public.i18n?.defaultLocale || "en";
// Show the default locale first so the label reads e.g. "EN/AR" (matches design).
const availableLocales = computed(() =>
  [...(locales.value as any[])].sort((a, b) =>
    a.code === defaultLocale ? -1 : b.code === defaultLocale ? 1 : 0
  )
);
const langLabel = computed(() =>
  availableLocales.value.map((l: any) => l.code.toUpperCase()).join("/")
);

const langOpen = ref(false);
const mobileOpen = ref(false);
const isScrolled = ref(false);

const isHome = computed(() => props.routePath === localePath("/"));
// Translucent over the hero only on the home page top; solid everywhere else.
const isSolid = computed(() => !isHome.value || isScrolled.value);

const closeLang = () => (langOpen.value = false);

const selectLang = async (code: string) => {
  langOpen.value = false;
  mobileOpen.value = false;
  if (code !== locale.value) {
    try {
      await setLocale(code);
    } catch (error) {
      console.error("Failed to switch language:", error);
    }
  }
};

const checkScroll = () => {
  isScrolled.value = document.body.scrollTop > 40 || window.scrollY > 40;
};

watch(
  () => props.routePath,
  () => {
    mobileOpen.value = false;
    checkScroll();
  }
);

onMounted(() => {
  checkScroll();
  document.body.addEventListener("scroll", checkScroll, { passive: true });
  window.addEventListener("scroll", checkScroll, { passive: true });
});
onUnmounted(() => {
  document.body.removeEventListener("scroll", checkScroll);
  window.removeEventListener("scroll", checkScroll);
});
</script>

<style scoped lang="scss">
.stacked-header {
  position: fixed;
  top: 0;
  inset-inline: 0;
  z-index: 40;
  font-family: var(--fontFamily, "Inter"), sans-serif;

  /* ---- Top utility bar ---- */
  &__topbar {
    background: #ffffff;
  }

  &__topbar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    padding-block: 9px;

    @media (max-width: 1023px) {
      min-height: 32px;
      padding-block: 0;
    }
  }

  &__contact {
    display: flex;
    align-items: center;
    gap: 30px;

    @media (max-width: 1023px) {
      gap: 12px;
    }
  }

  &__contact-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 1;
    color: var(--gray-1);
    white-space: nowrap;
  }

  &__contact-icon {
    width: 18px;
    height: 18px;

    @media (max-width: 1023px) {
      width: 16px;
      height: 16px;
    }
  }

  /* On very small screens the working hours would overflow — hide them. */
  @media (max-width: 400px) {
    .stacked-header__hours {
      display: none;
    }
  }

  &__socials {
    display: flex;
    align-items: center;
    gap: 20px;

    @media (max-width: 1023px) {
      gap: 12px;
    }

    a {
      display: inline-flex;
      color: var(--gray-1);
      font-size: 22px;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.7;
      }

      @media (max-width: 1023px) {
        font-size: 20px;
      }
    }
  }

  /* ---- Main nav ---- */
  &__nav {
    background: rgba(0, 0, 0, 0.2);
    transition: background-color 0.25s ease;

    @media (max-width: 1023px) {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  &__nav-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-block: 14px;

    @media (max-width: 1023px) {
      min-height: 56px;
      padding-block: 0;
    }
  }

  &__logo {
    display: inline-flex;
    align-items: center;
    height: 44px;

    @media (max-width: 1023px) {
      height: 34px;
    }

    img {
      height: 100%;
      width: auto;
      max-width: none;
      object-fit: contain;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 40px;

    @media (max-width: 1023px) {
      gap: 20px;
    }
  }

  &__links {
    display: flex;
    align-items: center;
    gap: 40px;

    @media (max-width: 1023px) {
      display: none;
    }
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    line-height: 14px;
    letter-spacing: 0.56px;
    text-transform: uppercase;
    color: var(--gray-6);
    white-space: nowrap;
    background: none;
    border: 0;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: var(--primary-2);
    }
  }

  &__chevron {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;

    @media (max-width: 1023px) {
      width: 18px;
      height: 18px;
    }
  }

  /* ---- Language dropdown ---- */
  &__lang {
    position: relative;
  }

  &__lang-menu {
    position: absolute;
    inset-inline-end: 0;
    top: calc(100% + 12px);
    min-width: 120px;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    padding: 6px;
    z-index: 50;

    li button {
      display: block;
      width: 100%;
      text-align: start;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      color: var(--primary-1);
      transition: background 0.15s ease;

      &:hover {
        background: var(--gray-5);
      }
      &.is-active {
        color: var(--primary-2);
        font-weight: 600;
      }
    }
  }

  /* ---- Hamburger (3 bars) ---- */
  &__burger {
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 24px;
    height: 24px;

    @media (max-width: 1023px) {
      display: flex;
    }

    span {
      display: block;
      width: 20px;
      height: 1.5px;
      border-radius: 1px;
      background: #ffffff;
      transition: transform 0.25s ease, opacity 0.25s ease;
    }

    &.is-open span:nth-child(1) {
      transform: translateY(5.5px) rotate(45deg);
    }
    &.is-open span:nth-child(2) {
      opacity: 0;
    }
    &.is-open span:nth-child(3) {
      transform: translateY(-5.5px) rotate(-45deg);
    }
  }

  /* ---- Mobile menu ---- */
  &__mobile {
    display: flex;
    flex-direction: column;
    background: var(--primary-1);
    padding: 8px 16px 20px;

    @media (min-width: 1024px) {
      display: none;
    }
  }

  &__mobile-link {
    padding: 12px 0;
    font-size: 14px;
    letter-spacing: 0.56px;
    text-transform: uppercase;
    color: var(--gray-6);
    text-align: start;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
}

.stacked-header--solid .stacked-header__nav {
  background: var(--primary-1);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.stacked-fade-enter-active,
.stacked-fade-leave-active {
  transition: opacity 0.2s ease;
}
.stacked-fade-enter-from,
.stacked-fade-leave-to {
  opacity: 0;
}
</style>
